import { useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Field } from '../../models/Field'
import { Option } from '../../models/Option'
import { generateRandomID } from '../../utils/numbers'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { Checkbox } from '../common/fields/Checkbox'
import { Select } from '../common/fields/Select'
import { TextField } from '../common/fields/TextField'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    isOpen: boolean
    onClose: () => void
    field: Field
    onDone: (updatedfield: Field) => void
    onEditOption?: (option: Option) => void
    title?: string
    allowTypeModification?: boolean
}

const fieldTypeArray = Array.from(Field.FIELD_TYPES.entries())

export const FieldModal = ({
    isOpen,
    onClose,
    field,
    onDone,
    onEditOption,
    title,
    allowTypeModification = true,
}: Props) => {
    const [scratchField, setScratchField] = useState<Field>(field)
    const [searchParams, _] = useSearchParams()

    const newOptionRef = useRef<HTMLInputElement>(null)

    const isNewField = searchParams.get('field') === 'new'

    const isDoneEnabled = useMemo(() => {
        return Boolean(scratchField.label) && Boolean(scratchField.type)
    }, [scratchField])

    const handleDone = () => {
        if (isNewField) {
            scratchField.id = `__new-${scratchField.label?.replaceAll(' ', '-')}`
        }
        onDone(scratchField)
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            footer={
                <Button onClick={handleDone} intent="primary" disabled={!isDoneEnabled}>
                    Save
                </Button>
            }
            width={744}
        >
            <div className="flex flex-col items-stretch justify-start gap-y-8">
                <WorkflowPage.IconSection title="Question (required)" icon="form2.large">
                    <TextField
                        size="medium"
                        placeholder="Enter question"
                        value={scratchField.label ?? ''}
                        onChange={(event) =>
                            setScratchField(scratchField.clone({ label: event.target.value }))
                        }
                        onClear={() => setScratchField(scratchField.clone({ label: '' }))}
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    title="Question type (required)"
                    icon="settings.large"
                    disabled={!isNewField && !allowTypeModification}
                >
                    <Select
                        isDisabled={!isNewField && !allowTypeModification}
                        size="medium"
                        selectedOptionID={scratchField.type}
                        onSelectOption={(option) =>
                            setScratchField(scratchField.clone({ type: option.id }))
                        }
                        options={fieldTypeArray.map(([key, value]) => ({
                            id: key,
                            title: value,
                        }))}
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    title="Required"
                    icon="exclamation.large"
                    description="Is this question required on this form?"
                    hasHoverEffect
                    secondaryView={
                        <Checkbox
                            isChecked={scratchField.required}
                            onChange={(event) => {
                                setScratchField(scratchField.clone({ is_required: event }))
                            }}
                        />
                    }
                />

                <WorkflowPage.IconSection
                    title="Show online"
                    icon="computerDollar.large"
                    description="Enable if you'd like clients to fill out this question"
                    hasHoverEffect
                    secondaryView={
                        <Checkbox
                            isChecked={scratchField.isPublic}
                            onChange={(event) => {
                                setScratchField(scratchField.clone({ is_public: event }))
                            }}
                        />
                    }
                />

                {scratchField.type?.includes('select') && (
                    <WorkflowPage.IconSection
                        title="Options"
                        icon="listPlus.large"
                        description="Add options to this question"
                    >
                        {allowTypeModification && (
                            <TextField
                                className="mb-3"
                                size="medium"
                                placeholder="Add option"
                                ref={newOptionRef}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' && newOptionRef.current?.value) {
                                        event.preventDefault()
                                        setScratchField((s) => {
                                            const newOptions = [
                                                ...s.options,
                                                new Option({
                                                    id: generateRandomID(),
                                                    label: newOptionRef.current?.value ?? '',
                                                    seqnum: s.options.length + 1,
                                                }),
                                            ]
                                            return s.clone({ options: newOptions })
                                        })
                                        newOptionRef.current.value = ''
                                    }
                                }}
                            />
                        )}

                        <WorkflowPage.ArrangeableCustomOptions
                            options={scratchField.options.map((o, idx) => ({
                                id: o.id ?? o.label ?? '',
                                title: o.label ?? '',
                                seqnum: o.seqnum ?? idx,
                                option: o,
                                onClick: allowTypeModification
                                    ? () => {
                                          onEditOption?.(o)
                                      }
                                    : undefined,
                                onDelete: allowTypeModification
                                    ? () =>
                                          setScratchField((s) => {
                                              const newOptions = s.options
                                                  .filter((f) => f.id !== o.id)
                                                  .map((f, idx) => f.clone({ seqnum: idx + 1 }))
                                              return s.clone({ options: newOptions })
                                          })
                                    : undefined,
                            }))}
                            droppableId={`question-builder-modal-options-${scratchField.id}`}
                            isDragDisabled={!isNewField && !allowTypeModification}
                            onDragEnd={(source, dest, list) => {
                                setScratchField(
                                    scratchField.clone({
                                        options: list.flatMap((o, idx) =>
                                            o.option.clone({ seqnum: idx + 1 })
                                        ),
                                    })
                                )
                            }}
                        />
                    </WorkflowPage.IconSection>
                )}
            </div>
        </Modal>
    )
}
